import {
  ULK_LAST_ATT_SHOULD_SEND_NOTIFICATION,
  ULK_LAST_BILL_RECEIPT_ITEM_KIND_LIST,
  ULK_LAST_POST_IS_STUDENT_READABLE,
  ULK_PREFERENCE_THEME,
  ULK_UI_STATUS_BILL_GROUP_LIST_VIEW,
  ULK_UI_STATUS_CLS_DRAWER_OPEN,
  ULK_UI_STATUS_LESSON_REPORT_WRITE_VIEW,
  ULK_UI_STATUS_STUDENT_DRAWER_OPEN,
  ULK_UI_STATUS_TEACHER_DRAWER_OPEN,
} from 'constants/frontend/app/localStorageKeys';
import { Bill } from 'constants/frontend/shared/Bill';
import { BillItemKindT } from 'types/shared/core/Bill';
import Token from './Token';

const LEGACY_THEME_KEY = 'theme'; // 20260925 이후 제거 가능
const LEGACY_BILL_RECEIPT_ITEM_KIND_LIST_KEY = 'billReceiptItemKindList'; // 20260925 이후 제거 가능

const camelCaseKeyPattern = /^[a-z][A-Za-z0-9]*$/;
const billReceiptItemKindList = Array.from(
  Bill.billItemKinds.keys(),
) as BillItemKindT[];

export type UserThemeModeT = 'light' | 'dark';

const getBrowserTheme = (): UserThemeModeT => {
  if (typeof window === 'undefined' || !window.matchMedia) return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
};

type UserLocalStorageValueByKeyT = {
  [ULK_PREFERENCE_THEME]: UserThemeModeT;
  [ULK_UI_STATUS_STUDENT_DRAWER_OPEN]: boolean;
  [ULK_UI_STATUS_TEACHER_DRAWER_OPEN]: boolean;
  [ULK_UI_STATUS_CLS_DRAWER_OPEN]: boolean;
  [ULK_UI_STATUS_LESSON_REPORT_WRITE_VIEW]: 'horizontal' | 'vertical';
  [ULK_UI_STATUS_BILL_GROUP_LIST_VIEW]: 'byYear' | 'byMonth';
  [ULK_LAST_ATT_SHOULD_SEND_NOTIFICATION]: boolean;
  [ULK_LAST_BILL_RECEIPT_ITEM_KIND_LIST]: BillItemKindT[];
  [ULK_LAST_POST_IS_STUDENT_READABLE]: boolean;
};

type UserLocalStorageKeyT = keyof UserLocalStorageValueByKeyT;
type UserLocalStorageValueT =
  UserLocalStorageValueByKeyT[UserLocalStorageKeyT];

type LegacyKeyConfigT = {
  key: string;
  scope: 'user' | 'global';
};

const keyAndDefaultValueMap = new Map<
  UserLocalStorageKeyT,
  UserLocalStorageValueT
>([
  [ULK_PREFERENCE_THEME, getBrowserTheme()],
  [ULK_UI_STATUS_STUDENT_DRAWER_OPEN, true],
  [ULK_UI_STATUS_TEACHER_DRAWER_OPEN, true],
  [ULK_UI_STATUS_CLS_DRAWER_OPEN, true],
  [ULK_UI_STATUS_LESSON_REPORT_WRITE_VIEW, 'horizontal'],
  [ULK_UI_STATUS_BILL_GROUP_LIST_VIEW, 'byYear'],
  [ULK_LAST_ATT_SHOULD_SEND_NOTIFICATION, true],
  [ULK_LAST_BILL_RECEIPT_ITEM_KIND_LIST, billReceiptItemKindList],
  [ULK_LAST_POST_IS_STUDENT_READABLE, true],
]);

const legacyKeyMap = new Map<UserLocalStorageKeyT, LegacyKeyConfigT[]>([
  [ULK_PREFERENCE_THEME, [{ key: LEGACY_THEME_KEY, scope: 'global' }]],
  [
    ULK_LAST_BILL_RECEIPT_ITEM_KIND_LIST,
    [
      {
        key: LEGACY_BILL_RECEIPT_ITEM_KIND_LIST_KEY,
        scope: 'global',
      },
    ],
  ],
]);

class UserLocalStorage {
  private static getDefaultValue<K extends UserLocalStorageKeyT>(
    key: K,
  ): UserLocalStorageValueByKeyT[K] {
    if (!keyAndDefaultValueMap.has(key)) {
      throw new Error(`Unknown UserLocalStorage key: ${key}`);
    }

    const defaultValue = keyAndDefaultValueMap.get(key);
    return (
      Array.isArray(defaultValue) ? [...defaultValue] : defaultValue
    ) as UserLocalStorageValueByKeyT[K];
  }

  private static buildUserKey(key: string): string | null {
    if (!camelCaseKeyPattern.test(key)) {
      throw new Error(`UserLocalStorage key must be camelCase: ${key}`);
    }

    const userId = Token.getCurrentUserId();
    if (!userId) return null;

    return `user${userId}${key.charAt(0).toUpperCase()}${key.slice(1)}`;
  }

  private static isValidValue<K extends UserLocalStorageKeyT>(
    key: K,
    value: unknown,
  ): value is UserLocalStorageValueByKeyT[K] {
    if (key === ULK_PREFERENCE_THEME) {
      return value === 'light' || value === 'dark';
    }

    if (key === ULK_UI_STATUS_LESSON_REPORT_WRITE_VIEW) {
      return value === 'horizontal' || value === 'vertical';
    }

    if (key === ULK_UI_STATUS_BILL_GROUP_LIST_VIEW) {
      return value === 'byYear' || value === 'byMonth';
    }

    if (key === ULK_LAST_BILL_RECEIPT_ITEM_KIND_LIST) {
      return (
        Array.isArray(value) &&
        value.length > 0 &&
        value.every((itemKind) => billReceiptItemKindList.includes(itemKind)) &&
        new Set(value).size === value.length
      );
    }

    return typeof value === typeof this.getDefaultValue(key);
  }

  private static parseStoredValue<K extends UserLocalStorageKeyT>(
    key: K,
    storedValue: string,
  ): UserLocalStorageValueByKeyT[K] | null {
    if (key === ULK_LAST_BILL_RECEIPT_ITEM_KIND_LIST) {
      const parsedValue = storedValue
        .split(',')
        .map((value) => value.trim());
      if (!this.isValidValue(key, parsedValue)) return null;

      const parsedValueSet = new Set(parsedValue);
      return billReceiptItemKindList.filter((itemKind) =>
        parsedValueSet.has(itemKind),
      ) as UserLocalStorageValueByKeyT[K];
    }

    let parsedValue: unknown;
    try {
      parsedValue = JSON.parse(storedValue);
    } catch {
      parsedValue = storedValue;
    }

    return this.isValidValue(key, parsedValue) ? parsedValue : null;
  }

  private static serializeValue<K extends UserLocalStorageKeyT>(
    key: K,
    value: UserLocalStorageValueByKeyT[K],
  ): string {
    if (key === ULK_LAST_BILL_RECEIPT_ITEM_KIND_LIST) {
      return (value as BillItemKindT[]).join(',');
    }

    return JSON.stringify(value);
  }

  private static migrateLegacyItem<K extends UserLocalStorageKeyT>(
    key: K,
    storageKey: string,
  ): UserLocalStorageValueByKeyT[K] | null {
    const legacyKeyConfigList = legacyKeyMap.get(key);
    if (!legacyKeyConfigList) return null;

    for (const legacyKeyConfig of legacyKeyConfigList) {
      const legacyStorageKey =
        legacyKeyConfig.scope === 'user'
          ? this.buildUserKey(legacyKeyConfig.key)
          : legacyKeyConfig.key;
      if (!legacyStorageKey) continue;

      try {
        const legacyStoredValue = localStorage.getItem(legacyStorageKey);
        if (legacyStoredValue === null) continue;

        const legacyValue = this.parseStoredValue(key, legacyStoredValue);
        if (legacyValue === null) continue;

        localStorage.setItem(storageKey, this.serializeValue(key, legacyValue));
        localStorage.removeItem(legacyStorageKey);
        return legacyValue;
      } catch {
        continue;
      }
    }

    return null;
  }

  static getItem<K extends UserLocalStorageKeyT>(
    key: K,
  ): UserLocalStorageValueByKeyT[K] {
    const defaultValue = this.getDefaultValue(key);
    const storageKey = this.buildUserKey(key);
    if (!storageKey) return defaultValue;

    try {
      const storedValue = localStorage.getItem(storageKey);
      if (storedValue !== null) {
        const parsedValue = this.parseStoredValue(key, storedValue);
        if (parsedValue !== null) return parsedValue;
      }

      return this.migrateLegacyItem(key, storageKey) ?? defaultValue;
    } catch {
      return defaultValue;
    }
  }

  static setItem<K extends UserLocalStorageKeyT>(
    key: K,
    value: UserLocalStorageValueByKeyT[K],
  ): boolean {
    if (!this.isValidValue(key, value)) {
      throw new Error(`Invalid UserLocalStorage value for key: ${key}`);
    }

    const storageKey = this.buildUserKey(key);
    if (!storageKey) return false;

    try {
      localStorage.setItem(storageKey, this.serializeValue(key, value));
      return true;
    } catch {
      return false;
    }
  }

  static removeItem(key: UserLocalStorageKeyT): boolean {
    this.getDefaultValue(key);
    const storageKey = this.buildUserKey(key);
    if (!storageKey) return false;

    try {
      localStorage.removeItem(storageKey);
      return true;
    } catch {
      return false;
    }
  }
}

export default UserLocalStorage;
